import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import type { ManualTeamInput, SeasonArchive, Team } from "@averagejoesff/shared";
import { upsertTeams } from "../../api/adminApi";
import { AdminFormStatus } from "../../components/AdminFormStatus";
import { useAdminForm } from "../../hooks/useAdminForm";

interface EditTeamsFormProps {
  season: number;
  teams: Team[];
  idToken: string;
  onSaved: (archive: SeasonArchive) => void;
  /** Extra control(s) rendered at the end of the action row, right-aligned. */
  children?: ReactNode;
}

/** A row's manager names are edited as one comma-separated field. */
interface Row {
  name: string;
  managers: string;
}

function toRows(teams: Team[]): Row[] {
  return teams.length > 0
    ? teams.map((t) => ({ name: t.name, managers: t.managerNames.join(", ") }))
    : [{ name: "", managers: "" }];
}

function toManualTeams(rows: Row[]): ManualTeamInput[] {
  return rows
    .filter((r) => r.name.trim() !== "")
    .map((r) => ({
      name: r.name.trim(),
      managerNames: r.managers
        .split(",")
        .map((m) => m.trim())
        .filter(Boolean),
    }));
}

export function EditTeamsForm({ season, teams, idToken, onSaved, children }: EditTeamsFormProps) {
  const [rows, setRows] = useState<Row[]>(() => toRows(teams));
  const { status, error, submit } = useAdminForm(
    () => upsertTeams(season, toManualTeams(rows), idToken),
    onSaved,
  );

  function updateRow(index: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  function addRow() {
    setRows((prev) => [...prev, { name: "", managers: "" }]);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  return (
    <form className="admin-panel__form" onSubmit={handleSubmit}>
      <ul className="admin-panel__team-list">
        {rows.map((row, i) => (
          <li key={i} className="admin-panel__team-row">
            <div className="admin-panel__team-row-fields">
              <input
                type="text"
                placeholder="Team name"
                value={row.name}
                onChange={(e) => updateRow(i, { name: e.target.value })}
              />
              <input
                type="text"
                placeholder="Manager(s), comma-separated"
                value={row.managers}
                onChange={(e) => updateRow(i, { managers: e.target.value })}
              />
            </div>
            <button
              type="button"
              className="admin-panel__remove"
              aria-label={`Remove ${row.name || "team"}`}
              onClick={() => removeRow(i)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <div className="admin-panel__actions">
        <button type="button" onClick={addRow}>
          + Add team
        </button>
        <button type="submit" disabled={status === "pending"}>
          {status === "pending" ? "Saving…" : "Save roster"}
        </button>
        <AdminFormStatus
          status={status}
          error={error}
          savedLabel="Saved — other viewers may take up to a minute to see it."
        />
        {children}
      </div>
    </form>
  );
}
